// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type BaseQueryEnhancer, type FetchArgs } from '@reduxjs/toolkit/query';

/**
 * Options for configuring multipart request handling.
 *
 * @remarks
 * Opt in per endpoint via `extraOptions`, mirroring {@link binaryQuery} for blob
 * responses. When enabled, `Content-Type` is omitted so the runtime sets the
 * multipart boundary for {@link FormData} bodies.
 */
export interface MultipartQueryOptions {
  readonly requestType?: 'formData';
}

const stripContentTypeHeader = (headers: HeadersInit | undefined): Headers => {
  const next = new Headers(headers);

  next.delete('Content-Type');
  next.delete('content-type');

  return next;
};

/**
 * Enhancer for multipart uploads used in RTK Query.
 *
 * @example
 *
 * ```typescript
 * createDocument: build.mutation({
 *   query: ({ body }) => ({
 *     url: '/documents/',
 *     method: 'POST',
 *     body: formDataFrom(body),
 *   }),
 *   extraOptions: {
 *     requestType: 'formData',
 *   },
 * }),
 * ```
 */
export const multipartQuery: BaseQueryEnhancer<
  FetchArgs,
  MultipartQueryOptions | void,
  void
> =
  baseQuery =>
  async (args, api, extraOptions): Promise<any> => {
    if (extraOptions?.requestType !== 'formData') {
      return baseQuery(args, api, extraOptions);
    }

    return baseQuery(
      {
        ...args,
        headers: stripContentTypeHeader(args.headers as HeadersInit | undefined),
      },
      api,
      extraOptions,
    );
  };
